import { Box, Button, Flex, Heading, Spinner, Stack, Text } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type ProcedureType,
  type SourcingEventDto,
  type SourcingEventPatchDto,
  useGetSourcingEventQuery,
  useListSourcingEventFieldSourcesQuery,
  useUpdateSourcingEventMutation,
} from "../../autogen/bff-api";
import { useCreateEditor, useSaveNow } from "../editing/save-coordinator";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { useApiError } from "../errors/useApiError";
import { SelectableCard } from "../input/SelectableCard";
import { useRunsPublicProcurements } from "../organization/useRunsPublicProcurements";
import { procedureTileKey } from "../procurement/publicProcurementOptions";
import { useSub } from "../subscription/useSub";
import { creationProgress } from "./creationAreas";
import type { ProcedureRule } from "./procurementRules";
import { procurementRulesFor } from "./procurementRules";
import { regulationConfirmationFor, type RecommendationReason, type RegulationConfirmation } from "./regulationConfirmation";

export type RegulationConfirmations = {
  event: SourcingEventDto;
  confirmation: RegulationConfirmation | null;
  reopened: RegulationConfirmation | null;
};

export const useRegulationConfirmation = (procurementId: string | null): RegulationConfirmations | null => {
  const { t } = useTranslation();
  const sub = useSub();
  const buyerRunsPublicProcurements = useRunsPublicProcurements();
  const buyerRegulation = useLoggedInWithOrgContextState().selectedOrg.defaultRegulation ?? null;
  const event = useGetSourcingEventQuery(procurementId ? { eventId: procurementId, recordView: false } : skipToken);
  const sources = useListSourcingEventFieldSourcesQuery(procurementId ? { eventId: procurementId } : skipToken);
  if (!procurementId || !event.data || !sources.data) return null;
  const regulation = creationProgress(event.data, sources.data.fields, {
    hasPublicProcurementAccess: buyerRunsPublicProcurements,
    hasProductAccess: sub.hasProductAccess,
  }).find((area) => area.area === "regulation");
  const context = event.data.eventContext;
  const rules = procurementRulesFor(event.data, t, new Date(), buyerRunsPublicProcurements, buyerRegulation);
  const confirmationWhen = (reopened: boolean) =>
    regulationConfirmationFor(rules, regulation?.status, context.type === "Public" ? context.procedureType : null, {
      buyerRunsPublicProcurements,
      outsideProcurementAct: event.data?.outsideProcurementAct ?? false,
      reopened,
    });
  return { event: event.data, confirmation: confirmationWhen(false), reopened: confirmationWhen(true) };
};

export const RegulationConfirmationCard = ({
  event,
  confirmation,
  isDisabled,
  onConfirmed,
}: {
  event: SourcingEventDto;
  confirmation: RegulationConfirmation;
  isDisabled: boolean;
  onConfirmed: (procedureName: string | null) => Promise<void> | void;
}) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [updateEvent] = useUpdateSourcingEventMutation();
  const editorId = `regulation-confirmation:${event.id}`;
  useCreateEditor<SourcingEventDto, SourcingEventPatchDto>(editorId, {
    initialState: event,
    save: (fields) => updateEvent({ eventId: event.id, sourcingEventPatchDto: fields }).unwrap(),
  });
  const { saveNow } = useSaveNow<SourcingEventDto, SourcingEventPatchDto>(editorId);
  const [choosesAnother, setChoosesAnother] = useState(false);
  const [saving, setSaving] = useState<ProcedureType | null>(null);
  const [confirmingWithoutProcedure, setConfirmingWithoutProcedure] = useState(false);
  const reasons: Record<RecommendationReason, (procedure: string) => string> = {
    open: (procedure) =>
      t(
        "{{procedure}} lets every interested supplier submit a tender, and is the most common procedure for this part of the regulations.",
        { procedure },
      ),
    alreadyChosen: () => t("This procedure is already chosen for the procurement."),
    firstAllowed: () => t("This is the first procedure the regulations allow for this procurement."),
  };
  const nameOf = (procedure: ProcedureRule) => String(t(procedureTileKey(procedure.procedureType)));

  const choose = async (procedure: ProcedureRule) => {
    setSaving(procedure.procedureType);
    try {
      await saveNow({ publicContext: { procedureType: procedure.procedureType } });
      await onConfirmed(nameOf(procedure));
    } catch (e) {
      trigger(e);
    } finally {
      setSaving(null);
    }
  };

  const confirmWithoutProcedure = async () => {
    setConfirmingWithoutProcedure(true);
    try {
      await onConfirmed(null);
    } finally {
      setConfirmingWithoutProcedure(false);
    }
  };

  return (
    <Box
      as="section"
      aria-label={String(t("Regulations and procedure"))}
      bg="smBackground"
      borderWidth="1px"
      borderColor="smBorder"
      borderRadius="xl"
      boxShadow="sm"
      p="4"
      mb="3"
    >
      <Heading as="h3" fontSize="md" fontWeight="semibold" color="smText" mb="3">
        {t("Regulations and procedure")}
      </Heading>
      {confirmation.kind !== "public" ? (
        <Stack spacing="3">
          <Text fontSize="sm" color="smText">
            {confirmation.kind === "privatePartI"
              ? t(
                  "This is a private competition in Part I. Part I prescribes no procedure, so there is no procedure to choose, but the basic principles of the Public Procurement Act (LOA § 4) apply.",
                )
              : t("This is a private competition. It follows no public procedure, so there is no procedure to choose.")}
          </Text>
          <Flex>
            <Button
              colorScheme="teal"
              size="sm"
              isLoading={confirmingWithoutProcedure}
              isDisabled={isDisabled || confirmingWithoutProcedure}
              onClick={() => void confirmWithoutProcedure()}
            >
              {t("Confirm")}
            </Button>
          </Flex>
        </Stack>
      ) : (
        <Stack spacing="3">
          {confirmation.classification && (
            <Fact label={t("Part of the regulations")} value={confirmation.classification} />
          )}
          <Fact label={t("Procedure")} value={nameOf(confirmation.recommended.procedure)} />
          <Fact
            label={t("Why")}
            value={reasons[confirmation.recommended.reason](nameOf(confirmation.recommended.procedure))}
          />
          {confirmation.recommended.procedure.legalBasis && (
            <Fact label={t("Legal basis")} value={confirmation.recommended.procedure.legalBasis} />
          )}
          <Flex gap="3" flexWrap="wrap">
            <Button
              colorScheme="teal"
              size="sm"
              isLoading={saving === confirmation.recommended.procedure.procedureType}
              isDisabled={isDisabled || saving !== null}
              onClick={() => void choose(confirmation.recommended.procedure)}
            >
              {t("Confirm")}
            </Button>
            {confirmation.alternatives.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                isDisabled={isDisabled || saving !== null}
                aria-expanded={choosesAnother}
                onClick={() => setChoosesAnother((shown) => !shown)}
              >
                {t("Choose another")}
              </Button>
            )}
          </Flex>
          {choosesAnother && (
            <Stack spacing="2" role="group" aria-label={String(t("Other procedures the regulations allow"))}>
              {confirmation.alternatives.map((procedure) => (
                <SelectableCard
                  key={procedure.procedureType}
                  isSelected={false}
                  isDisabled={isDisabled || saving !== null}
                  w="full"
                  onClick={() => void choose(procedure)}
                >
                  <Flex gap="3" align="center" justify="space-between">
                    <Box textAlign="left">
                      <Text fontSize="sm" color="smText" fontWeight="medium">
                        {nameOf(procedure)}
                      </Text>
                      {procedure.legalBasis && (
                        <Text fontSize="sm" color="smMuted">
                          {procedure.legalBasis}
                        </Text>
                      )}
                    </Box>
                    {saving === procedure.procedureType && <Spinner size="sm" color="smPrimary" />}
                  </Flex>
                </SelectableCard>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
};

const Fact = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Text fontSize="xs" color="smMuted">
      {label}
    </Text>
    <Text fontSize="sm" color="smText">
      {value}
    </Text>
  </Box>
);
